import React from 'react';
import { css } from '@emotion/react';
import { posthog } from 'posthog-js';
import { spacing, colors, fontStyles, borderRadius } from '../../../styles';
import { appsflyerEvents } from '../../../utils/appsflyerEvents';
import { InvestorType } from '../../../utils/smartChatPrompts';

interface InvestorTypeOption {
  value: InvestorType;
  label: string;
  description: string;
  emoji: string;
}

interface InvestorTypeSelectorProps {
  onSelect: (values: InvestorType[]) => void;
  selected?: InvestorType[];
}

const InvestorTypeSelectorComponent: React.FC<InvestorTypeSelectorProps> = ({ onSelect, selected = [] }) => {
  const options: InvestorTypeOption[] = [
    {
      value: 'new-investor',
      emoji: '🌱',
      label: 'Learning the basics',
      description: 'Get beginner-friendly explanations',
    },
    {
      value: 'active-trader',
      emoji: '📈',
      label: 'Active trading',
      description: 'Focus on news, technicals, and momentum',
    },
    {
      value: 'researcher',
      emoji: '🔍',
      label: 'Deep research',
      description: 'Dive through earnings calls and fundamentals',
    },
  ];

  const handleSelect = (value: InvestorType) => {
    // Single select - replace any existing selection
    const newSelected: InvestorType[] = [value];

    // Track motivation selection with PostHog
    const selectedOption = options.find((option) => option.value === value);
    posthog.capture('Onboarding Investor Type Selected', {
      investorType: value,
      label: selectedOption?.label || value,
    });

    // Track with AppsFlyer
    appsflyerEvents.onboarding.motivationSelected(value, 1).catch(console.error);

    onSelect(newSelected);
  };

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${spacing.deci};
      `}
    >
      {options.map((option) => {
        const isSelected = selected.includes(option.value);

        return (
          <label
            key={option.value}
            css={css`
              position: relative;
              display: flex;
              align-items: center;
              padding: ${spacing.deci};
              border: 2px solid ${isSelected ? colors.petrolBlue : colors.cloud};
              border-radius: ${borderRadius};
              background-color: ${isSelected ? colors.skyAir : 'transparent'};
              cursor: pointer;
              transition: all 0.2s ease;
              ${fontStyles.dog}
              text-align: left;

              &:hover {
                border-color: ${colors.petrolBlue};
              }

              html[data-theme='dark'] & {
                border-color: ${isSelected ? colors.skyBlue : colors.tungsten};
                background-color: ${isSelected ? 'rgba(40, 181, 189, 0.1)' : 'transparent'};
                color: ${colors.sugar};
              }
            `}
          >
            <div
              css={css`
                width: 20px;
                height: 20px;
                min-width: 20px;
                min-height: 20px;
                flex-shrink: 0;
                border: 2px solid ${isSelected ? colors.petrolBlue : colors.cloud};
                border-radius: 50%;
                margin-right: ${spacing.deci};
                position: relative;
                display: flex;
                align-items: center;
                justify-content: center;

                ${isSelected &&
                css`
                  border-color: ${colors.petrolBlue};

                  &::after {
                    content: '';
                    width: 10px;
                    height: 10px;
                    background-color: ${colors.petrolBlue};
                    border-radius: 50%;
                  }
                `}

                html[data-theme='dark'] & {
                  border-color: ${isSelected ? colors.skyBlue : colors.tungsten};

                  ${isSelected &&
                  css`
                    &::after {
                      background-color: ${colors.skyBlue};
                    }
                  `}
                }
              `}
            />
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 2px;
              `}
            >
              <span
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 6px;
                `}
              >
                <span>{option.emoji}</span>
                <span>{option.label}</span>
              </span>
              <span
                css={css`
                  ${fontStyles.cat};
                  color: ${colors.silver};

                  html[data-theme='dark'] & {
                    color: ${colors.cloud};
                  }
                `}
              >
                {option.description}
              </span>
            </div>

            <input
              type="radio"
              name="investor-type"
              value={option.value}
              checked={isSelected}
              onChange={() => handleSelect(option.value)}
              css={css`
                position: absolute;
                opacity: 0;
                width: 100%;
                height: 100%;
                top: 0;
                left: 0;
                cursor: pointer;
                z-index: 1;
              `}
            />
          </label>
        );
      })}

      {/* Subtle social proof badge */}
      <div
        css={css`
          text-align: center;
          margin-top: ${spacing.centi};
          ${fontStyles.cat};
          color: ${colors.silver};

          html[data-theme='dark'] & {
            color: ${colors.cloud};
          }
        `}
      >
        Trusted by 10,000+ investors
      </div>
    </div>
  );
};

export const InvestorTypeSelector = React.memo(InvestorTypeSelectorComponent);
